import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://simulador-cs-back.vercel.app/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const adminClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth client for auth endpoints (same base URL now, no authentication needed)
const authClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.url, response.data);
    return response.data;
  },
  (error) => {
    // Handle common errors
    console.error('❌ API Error:', error.config?.url, error.response?.status, error.message);
    if (error.response?.status === 404) {
      console.error('Resource not found');
    } else if (error.response?.status >= 500) {
      console.error('Server error');
    }
    
    return Promise.reject(error);
  }
);

// Auth client response interceptor
authClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    return Promise.reject(error);
  }
);

adminClient.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error)
);

const uploadFileToPresignedUrl = async (presignedUrl, file, contentType) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error('No se pudo subir el archivo a R2.');
  }
};

// API service object
export const api = {
  // Project access endpoints
  verifyProjectAccess: (projectId, accessCode, userEmail = null, userName = null) => {
    return authClient.post(`/auth/projects/${projectId}/verify-access/`, { 
      access_code: accessCode,
      user_email: userEmail,
      user_name: userName
    });
  },
  
  checkProjectAccess: (projectId, userEmail = null) => {
    return authClient.post(`/auth/projects/${projectId}/check-access/`, { 
      email: userEmail 
    });
  },
  
  getUserProjectAccesses: (userEmail) => {
    return authClient.get(`/auth/project-accesses/?email=${encodeURIComponent(userEmail)}`);
  },
  
  // Projects endpoints
  getProjects: (params = {}) => {
    return apiClient.get('/projects/', { params });
  },
  
  getProject: (id) => {
    return apiClient.get(`/projects/${id}/`);
  },
  
  getProjectStats: () => {
    return apiClient.get('/projects/stats/');
  },

  // Información financiera pública del proyecto
  getProjectFinancialInfo: (projectId, userEmail = null) => {
    return apiClient.post(`/projects/${projectId}/financial/`, {
      email: userEmail,
    });
  },
  
  // Simulations endpoints
  createSimulation: (data) => {
    return apiClient.post('/simulations/create/', data);
  },
  
  compareSimulations: (data) => {
    return apiClient.post('/simulations/compare/', data);
  },
  
  getSimulation: (id) => {
    return apiClient.get(`/simulations/${id}/`);
  },
  
  getUserSimulations: (userEmail) => {
    return apiClient.get(`/simulations/user/?email=${encodeURIComponent(userEmail)}`);
  },
  
  getSimulationStats: () => {
    return apiClient.get('/simulations/stats/');
  },
  
  // Tariff categories endpoints
  getTariffCategories: (projectId = null) => {
    return apiClient.get('/tariff-categories/', {
      params: projectId ? { project_id: projectId } : {},
    });
  },
  
  // Exchange rates endpoints
  getExchangeRates: () => {
    return apiClient.get('/exchange-rates/');
  },
  
  getCurrentExchangeRate: () => {
    return apiClient.get('/exchange-rate/current/');
  },
  
  calculateLimits: (data) => {
    return apiClient.post('/calculate-limits/', data);
  },
  
  // Core endpoints
  getSiteSettings: () => {
    return apiClient.get('/settings/');
  },
  
  sendContactMessage: (data) => {
    return apiClient.post('/contact/', data);
  },
  
  subscribeNewsletter: (data) => {
    return apiClient.post('/newsletter/subscribe/', data);
  },
  
  unsubscribeNewsletter: (data) => {
    return apiClient.post('/newsletter/unsubscribe/', data);
  },
  
  getHealthCheck: () => {
    return apiClient.get('/health/');
  },
  
  getApiInfo: () => {
    return apiClient.get('/info/');
  },

  // Custom administration endpoints
  adminLogin: (credentials) => {
    return adminClient.post('/admin/login/', credentials);
  },

  adminMe: () => {
    return adminClient.get('/admin/me/');
  },

  adminList: (resource) => {
    return adminClient.get(`/admin/${resource}/`);
  },

  adminCreate: (resource, data) => {
    return adminClient.post(`/admin/${resource}/`, data);
  },

  adminUpdate: (resource, id, data) => {
    return adminClient.patch(`/admin/${resource}/${id}/`, data);
  },

  adminDelete: (resource, id) => {
    return adminClient.delete(`/admin/${resource}/${id}/`);
  },

  adminUploadProjectLogo: (projectId, type, file) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);
    return adminClient.post(`/admin/projects/${projectId}/logo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminPrepareProjectLogoUpload: (projectId, type, file) => {
    return adminClient.post(`/admin/projects/${projectId}/logo/presigned/`, {
      type,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
    });
  },

  adminDeleteProjectLogo: (projectId, type) => {
    return adminClient.delete(`/admin/projects/${projectId}/logo/delete/`, { params: { type } });
  },

  adminListProjectTariffPrices: (projectId) => {
    return adminClient.get(`/admin/projects/${projectId}/tariff-prices/`);
  },

  adminCreateProjectTariffPrice: (projectId, data) => {
    return adminClient.post(`/admin/projects/${projectId}/tariff-prices/`, data);
  },

  adminUpdateProjectTariffPrice: (projectId, tariffPriceId, data) => {
    return adminClient.patch(`/admin/projects/${projectId}/tariff-prices/${tariffPriceId}/`, data);
  },

  adminDeleteProjectTariffPrice: (projectId, tariffPriceId) => {
    return adminClient.delete(`/admin/projects/${projectId}/tariff-prices/${tariffPriceId}/`);
  },

  adminListProjectParticipationSales: (projectId) => {
    return adminClient.get(`/admin/projects/${projectId}/participation-sales/`);
  },

  adminCreateProjectParticipationSale: (projectId, data) => {
    return adminClient.post(`/admin/projects/${projectId}/participation-sales/`, data);
  },

  adminUpdateProjectParticipationSale: (projectId, saleId, data) => {
    return adminClient.patch(`/admin/projects/${projectId}/participation-sales/${saleId}/`, data);
  },

  adminDeleteProjectParticipationSale: (projectId, saleId) => {
    return adminClient.delete(`/admin/projects/${projectId}/participation-sales/${saleId}/`);
  },

  adminUploadProjectParticipationSaleLogo: (projectId, saleId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return adminClient.post(`/admin/projects/${projectId}/participation-sales/${saleId}/logo/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminDeleteProjectParticipationSaleLogo: (projectId, saleId) => {
    return adminClient.delete(`/admin/projects/${projectId}/participation-sales/${saleId}/logo/delete/`);
  },

  adminListProjectPromotionalPriceRanges: (projectId) => {
    return adminClient.get(`/admin/projects/${projectId}/promotional-price-ranges/`);
  },

  adminCreateProjectPromotionalPriceRange: (projectId, data) => {
    return adminClient.post(`/admin/projects/${projectId}/promotional-price-ranges/`, data);
  },

  adminUpdateProjectPromotionalPriceRange: (projectId, rangeId, data) => {
    return adminClient.patch(`/admin/projects/${projectId}/promotional-price-ranges/${rangeId}/`, data);
  },

  adminDeleteProjectPromotionalPriceRange: (projectId, rangeId) => {
    return adminClient.delete(`/admin/projects/${projectId}/promotional-price-ranges/${rangeId}/`);
  },

  adminCreateProjectImage: (projectId, data) => {
    return adminClient.post(`/admin/projects/${projectId}/images/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminPrepareProjectImageUpload: (projectId, file, fields = {}) => {
    return adminClient.post(`/admin/projects/${projectId}/images/presigned/`, {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      ...fields,
    });
  },

  adminUpdateProjectImage: (projectId, imageId, data) => {
    return adminClient.patch(`/admin/projects/${projectId}/images/${imageId}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminDeleteProjectImage: (projectId, imageId) => {
    return adminClient.delete(`/admin/projects/${projectId}/images/${imageId}/`);
  },

  adminCreateProjectVideo: (projectId, data) => {
    return adminClient.post(`/admin/projects/${projectId}/videos/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminPrepareProjectVideoUpload: (projectId, file, fields = {}) => {
    return adminClient.post(`/admin/projects/${projectId}/videos/presigned/`, {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      ...fields,
    });
  },

  uploadFileToPresignedUrl,

  adminUpdateProjectVideo: (projectId, videoId, data) => {
    return adminClient.patch(`/admin/projects/${projectId}/videos/${videoId}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  adminDeleteProjectVideo: (projectId, videoId) => {
    return adminClient.delete(`/admin/projects/${projectId}/videos/${videoId}/`);
  },

  adminGetSiteSettings: () => {
    return adminClient.get('/admin/site-settings/');
  },

  adminUpdateSiteSettings: (data) => {
    return adminClient.patch('/admin/site-settings/', data);
  },
};

// Utility functions for API calls
export const apiUtils = {
  // Handle API errors and extract meaningful messages
  extractErrorMessage: (error) => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    
    if (error.response?.data?.errors) {
      // Handle validation errors
      const errors = error.response.data.errors;
      const firstField = Object.keys(errors)[0];
      return errors[firstField][0] || 'Error de validación';
    }

    if (error.response?.data && typeof error.response.data === 'object') {
      const firstField = Object.keys(error.response.data)[0];
      const fieldError = error.response.data[firstField];
      if (Array.isArray(fieldError)) {
        return fieldError[0] || 'Error de validación';
      }
      if (typeof fieldError === 'string') {
        return fieldError;
      }
    }
    
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'Error inesperado del servidor';
  },
  
  // Format currency values
  formatCurrency: (amount, currency = 'ARS') => {
    const formatter = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    return formatter.format(amount);
  },
  
  // Format large numbers with units
  formatNumber: (number, decimals = 0) => {
    const formatter = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    
    return formatter.format(number);
  },
  
  // Format power values with appropriate units
  formatPower: (power) => {
    if (power >= 1000) {
      const value = power / 1000;
      const decimals = value % 1 === 0 ? 0 : 2;
      return `${apiUtils.formatNumber(value, decimals)} MW`;
    }
    const decimals = power % 1 === 0 ? 0 : 2;
    return `${apiUtils.formatNumber(power, decimals)} kW`;
  },
  
  // Format energy values with appropriate units
  formatEnergy: (energy) => {
    if (energy >= 1000000) {
      return `${apiUtils.formatNumber(energy / 1000000, 1)} GWh`;
    }
    if (energy >= 1000) {
      return `${apiUtils.formatNumber(energy / 1000, 1)} MWh`;
    }
    return `${apiUtils.formatNumber(energy, 0)} kWh`;
  },
  
  // Convert USD to ARS using current exchange rate
  convertToARS: (usdAmount, exchangeRate) => {
    return usdAmount * exchangeRate;
  },
  
  // Convert ARS to USD using current exchange rate
  convertToUSD: (arsAmount, exchangeRate) => {
    return arsAmount / exchangeRate;
  },
  
  // Calculate percentage
  calculatePercentage: (value, total) => {
    if (total === 0) return 0;
    return (value / total) * 100;
  },
  
  // Format dates
  formatDate: (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  },
  
  // Format relative time
  formatRelativeTime: (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Hace un momento';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `Hace ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `Hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `Hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
    }
    
    return apiUtils.formatDate(dateString);
  },

  // Get project featured image
  getProjectFeaturedImage: (project) => {
    // If project has images array, return the featured one or the first one
    if (project.images && project.images.length > 0) {
      const featuredImage = project.images.find(img => img.is_featured);
      return featuredImage
        ? featuredImage.image_url || featuredImage.image
        : project.images[0].image_url || project.images[0].image;
    }
    
    // If project has a single image field
    if (project.image) {
      return project.image;
    }
    
    // Return placeholder if no image found
    return '/images/backgrounds/aereaparquesolar.jpg';
  },

  // Get project image URL
  getProjectImageUrl: (image) => {
    // If image is already a full URL, return it
    if (image && (image.startsWith('http://') || image.startsWith('https://'))) {
      return image;
    }
    
    // If image starts with /, it's already a relative path
    if (image && image.startsWith('/')) {
      return image;
    }
    
    // If image is just a filename or path, prepend the base path
    if (image) {
      return `/images/projects/${image}`;
    }
    
    // Return placeholder if no image
    return '/images/backgrounds/aereaparquesolar.jpg';
  },
};

// Helper para extraer mensajes de error
export const extractErrorMessage = (error) => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error.response?.data?.errors) {
    // Handle validation errors
    const errors = error.response.data.errors;
    const firstField = Object.keys(errors)[0];
    return errors[firstField][0] || 'Error de validación';
  }

  if (error.response?.data && typeof error.response.data === 'object') {
    const firstField = Object.keys(error.response.data)[0];
    const fieldError = error.response.data[firstField];
    if (Array.isArray(fieldError)) {
      return fieldError[0] || 'Error de validación';
    }
    if (typeof fieldError === 'string') {
      return fieldError;
    }
  }
  
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'Error inesperado del servidor';
};

export default api;